// frontend/src/pages/admin/FacturesAdmin.jsx
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import './FacturesAdmin.css';

const TABS = [
  { key: '',           label: 'Toutes'        },
  { key: 'submitted',  label: 'Soumises'      },
  { key: 'processing', label: 'En traitement' },
  { key: 'paid',       label: 'Payées'        },
  { key: 'rejected',   label: 'Rejetées'      },
];

const STATUS = {
  submitted:  { label: 'Soumise',       cls: 'badge-submitted'  },
  processing: { label: 'En traitement', cls: 'badge-processing' },
  paid:       { label: 'Virée',         cls: 'badge-paid'       },
  rejected:   { label: 'Rejetée',       cls: 'badge-rejected'   },
};

const fmt      = n => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 });
const fmtDate  = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtMonth = m => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(y, mo - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

export default function FacturesAdmin() {
  const [invoices, setInvoices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [tab, setTab]               = useState('');
  const [modal, setModal]           = useState(null); // { type: 'pay'|'reject', invoice }
  const [virRef, setVirRef]         = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [msg, setMsg]               = useState(null);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab ? { status: tab } : {};
      const res = await API.get('/admin/invoices', { params });
      setInvoices(res.data.invoices || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4500);
  };

  const handleProcess = async (id) => {
    try {
      await API.put(`/admin/invoices/${id}/process`);
      showMsg('success', 'Facture marquée "En traitement"');
      fetchInvoices();
    } catch { showMsg('error', 'Erreur'); }
  };

  const handlePay = async () => {
    setActionLoading(true);
    try {
      await API.put(`/admin/invoices/${modal.invoice.id}/pay`, { virement_ref: virRef });
      showMsg('success', 'Virement enregistré avec succès');
      setModal(null); setVirRef('');
      fetchInvoices();
    } catch { showMsg('error', 'Erreur'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      await API.put(`/admin/invoices/${modal.invoice.id}/reject`, { admin_note: rejectNote });
      showMsg('success', 'Facture rejetée');
      setModal(null); setRejectNote('');
      fetchInvoices();
    } catch { showMsg('error', 'Erreur'); }
    finally { setActionLoading(false); }
  };

  const downloadInvoice = (inv) => {
    const token = localStorage.getItem('token');
    const base  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    window.open(`${base}/admin/invoices/${inv.id}/download?token=${token}`, '_blank');
  };

  const totalCommission = invoices.reduce((s, i) => s + Number(i.commission_due || 0), 0);

  return (
    <div>
      {/* ── Header card ── */}
      <div className="fa-header-card admin-table-card">
        <div className="fa-header-left">
          <i className="fas fa-file-invoice-dollar fa-icon-cyan"></i>
          <div>
            <h2>Factures partenaires</h2>
            <p>Gérez les factures soumises et enregistrez les virements</p>
          </div>
        </div>
        <div className="fa-header-right">
          <div className="fa-stat">
            <span>{invoices.length}</span>
            <label>factures</label>
          </div>
          <div className="fa-stat fa-stat--cyan">
            <span>{fmt(totalCommission)} €</span>
            <label>commission totale</label>
          </div>
        </div>
      </div>

      {/* ── Alert ── */}
      <AnimatePresence>
        {msg && (
          <motion.div
            className={`fa-alert fa-alert--${msg.type}`}
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          >
            <i className={`fas ${msg.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            {msg.text}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Tabs ── */}
      <div className="fa-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`fa-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'submitted' && invoices.filter(i => i.status === 'submitted').length > 0 && (
              <span className="fa-tab-badge">{invoices.filter(i => i.status === 'submitted').length}</span>
            )}
          </button>
        ))}
        <button className="admin-btn-secondary" style={{ marginLeft: 'auto' }} onClick={fetchInvoices}>
          <i className="fas fa-sync-alt"></i> Actualiser
        </button>
      </div>

      {/* ── Table ── */}
      <div className="admin-table-card">
        {loading ? (
          <div style={{ padding: '40px 0', textAlign: 'center', color: '#94a3b8' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: 20, color: '#06b6d4', marginBottom: 8, display: 'block' }}></i>
            Chargement…
          </div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Partenaire</th>
                <th>Mois</th>
                <th>CA B2C (EUR)</th>
                <th>Commission due</th>
                <th>Statut</th>
                <th>Soumise le</th>
                <th>Virement prévu</th>
                <th>Référence</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => {
                const st = STATUS[inv.status] || { label: inv.status, cls: '' };
                return (
                  <tr key={inv.id}>
                    <td>
                      <div className="fa-partner-cell">
                        <strong>{inv.company_name}</strong>
                        <span>{inv.email}</span>
                      </div>
                    </td>
                    <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{fmtMonth(inv.month)}</td>
                    <td>{fmt(inv.ca_eur)}</td>
                    <td><strong style={{ color: '#06b6d4' }}>{fmt(inv.commission_due)} €</strong></td>
                    <td><span className={`fa-badge ${st.cls}`}>{st.label}</span></td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{fmtDate(inv.submitted_at)}</td>
                    <td style={{ fontSize: 12, color: '#64748b' }}>{fmtDate(inv.due_date)}</td>
                    <td>
                      {inv.virement_ref
                        ? <span className="fa-ref">{inv.virement_ref}</span>
                        : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td>
                      <div className="fa-actions">
                        {/* Download */}
                        {inv.invoice_filename && (
                          <button className="admin-btn-icon" title="Télécharger la facture PDF" onClick={() => downloadInvoice(inv)}>
                            <i className="fas fa-download"></i>
                          </button>
                        )}
                        {/* Process */}
                        {inv.status === 'submitted' && (
                          <button className="admin-btn-icon fa-btn-process" title="Marquer En traitement" onClick={() => handleProcess(inv.id)}>
                            <i className="fas fa-hourglass-half"></i>
                          </button>
                        )}
                        {/* Pay */}
                        {(inv.status === 'submitted' || inv.status === 'processing') && (
                          <button className="admin-btn-icon approve" title="Enregistrer le virement" onClick={() => { setModal({ type: 'pay', invoice: inv }); setVirRef(''); }}>
                            <i className="fas fa-paper-plane"></i>
                          </button>
                        )}
                        {/* Reject */}
                        {(inv.status === 'submitted' || inv.status === 'processing') && (
                          <button className="admin-btn-icon reject" title="Rejeter" onClick={() => { setModal({ type: 'reject', invoice: inv }); setRejectNote(''); }}>
                            <i className="fas fa-times"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {invoices.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', color: '#94a3b8', padding: '36px 0' }}>Aucune facture trouvée</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Modal Pay ── */}
      <AnimatePresence>
        {modal?.type === 'pay' && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="admin-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>
              <div className="admin-modal-header">
                <h2><i className="fas fa-paper-plane" style={{ color: '#06b6d4', marginRight: 8 }}></i>Enregistrer le virement</h2>
                <button className="admin-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>
              <div style={{ padding: '0 24px 8px' }}>
                <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
                  Virement pour <strong>{modal.invoice.company_name}</strong> — {fmtMonth(modal.invoice.month)}<br />
                  Montant : <strong style={{ color: '#06b6d4' }}>{fmt(modal.invoice.commission_due)} EUR</strong>
                </p>
                <div className="admin-form-group">
                  <label>Référence du virement</label>
                  <input
                    className="admin-input"
                    placeholder="Ex: VIR-2025-03-ACME"
                    value={virRef}
                    onChange={e => setVirRef(e.target.value)}
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="admin-btn-secondary" onClick={() => setModal(null)}>Annuler</button>
                <button className="admin-btn-primary" onClick={handlePay} disabled={actionLoading}>
                  {actionLoading ? <><i className="fas fa-spinner fa-spin"></i> Enregistrement…</> : <><i className="fas fa-check"></i> Confirmer le virement</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Modal Reject ── */}
      <AnimatePresence>
        {modal?.type === 'reject' && (
          <motion.div className="admin-modal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={e => e.target === e.currentTarget && setModal(null)}>
            <motion.div className="admin-modal" initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}>
              <div className="admin-modal-header">
                <h2><i className="fas fa-times-circle" style={{ color: '#ef4444', marginRight: 8 }}></i>Rejeter la facture</h2>
                <button className="admin-modal-close" onClick={() => setModal(null)}><i className="fas fa-times"></i></button>
              </div>
              <div style={{ padding: '0 24px 8px' }}>
                <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
                  Rejet de la facture de <strong>{modal.invoice.company_name}</strong> — {fmtMonth(modal.invoice.month)}
                </p>
                <div className="admin-form-group">
                  <label>Motif du rejet (visible par le partenaire)</label>
                  <textarea
                    className="admin-input"
                    rows={3}
                    placeholder="Ex: Facture illisible, montant incorrect…"
                    value={rejectNote}
                    onChange={e => setRejectNote(e.target.value)}
                    style={{ resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button className="admin-btn-secondary" onClick={() => setModal(null)}>Annuler</button>
                <button className="admin-btn-danger" onClick={handleReject} disabled={actionLoading}>
                  {actionLoading ? <><i className="fas fa-spinner fa-spin"></i> Rejet…</> : <><i className="fas fa-times"></i> Rejeter</>}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
